import React, { useState, useEffect } from 'react';
import { connect, PromiseState } from 'react-refetch';

import getCustomListsURL from 'url-constructors/getCustomListsURL';
import { TEntityInfo, ICustomListData } from './shared';
import AddToList from './AddToList';
import { EntityTypes } from 'context/entityContext';
import EditList from '../EditList';

import getCustomListEntitiesUrl from 'url-constructors/getCustomListEntitiesUrl';

interface IOuterProps {
  entityName: string;
  entityId: string;
  entityType: EntityTypes;
  close: () => void;
}

interface IAddEntityToListViewProps extends IOuterProps {
  fetchCustomLists: () => any;
  postCustomListEntities: (entities: TEntityInfo[], listId: string) => any;
  postDeleteCustomListEntities: (
    entities: TEntityInfo[],
    listId: string
  ) => any;
  postCustomEntityList: (
    list: ICustomListData,
    successMethod: () => void,
    errorMethod?: () => void
  ) => any;
  customListsData: PromiseState<any>;
  tempCustomListsData: PromiseState<any>;
}

const datasources = (): any => {
  return {
    fetchCustomLists: (): any => {
      return {
        customListsData: getCustomListsURL(),
      };
    },
    postCustomListEntities(entities: TEntityInfo[], listId: string): any {
      return {
        customListsData: {
          url: getCustomListEntitiesUrl({
            listId,
          }),
          method: 'POST',
          body: JSON.stringify(entities),
          andCatch: (): any => {
            return {
              tempCustomListsData: {
                url: getCustomListsURL(),
              },
            };
          },
          andThen: (): any => ({
            customListsData: {
              url: getCustomListsURL(),
              force: true,
              refreshing: true,
            },
          }),
        },
      };
    },
    postCustomEntityList(
      list: ICustomListData,
      successMethod: () => void,
      errorMethod?: () => void
    ): any {
      return {
        customListsData: {
          url: `/api/customEntityLists`,
          method: 'POST',
          body: JSON.stringify(list),
          andThen: (): any => ({
            customListsData: {
              url: getCustomListsURL(),
              force: true,
              refreshing: true,
            },
          }),
          catch: (): void => errorMethod && errorMethod(),
          then: successMethod,
        },
      };
    },
    postDeleteCustomListEntities(
      entities: TEntityInfo[],
      listId: string,
      successMethod: () => void,
      errorMethod?: () => void
    ): any {
      return {
        customListsData: {
          url: getCustomListEntitiesUrl({
            listId,
          }),
          method: 'DELETE',
          body: JSON.stringify(entities),
          andThen: (): any => ({
            customListsData: {
              url: getCustomListsURL(),
              force: true,
              refreshing: true,
            },
          }),
          catch: (): void => errorMethod && errorMethod(),
          then: successMethod,
        },
      };
    },
  };
};

type ViewStates = 'ADD_TO_LIST' | 'CREATE_LIST';

const AddEntityToListView: React.FC<IAddEntityToListViewProps> = (props) => {
  const {
    entityName,
    entityId,
    entityType,
    close,
    fetchCustomLists,
    postCustomListEntities,
    postDeleteCustomListEntities,
    postCustomEntityList,
    customListsData = PromiseState.create(),
    tempCustomListsData = PromiseState.create(),
  } = props;

  useEffect(() => {
    fetchCustomLists();
  }, [fetchCustomLists]);

  const [viewState, setViewState] = useState<ViewStates>('ADD_TO_LIST');

  switch (viewState) {
    case 'ADD_TO_LIST':
      return (
        <AddToList
          error={customListsData.reason}
          entityId={entityId}
          entityType={entityType}
          entityName={entityName}
          close={close}
          addEntityToList={postCustomListEntities}
          deleteEntityFromList={postDeleteCustomListEntities}
          customListsData={
            customListsData.rejected ? tempCustomListsData : customListsData
          }
          switchToCreateListView={(): void => {
            setViewState('CREATE_LIST');
          }}
        />
      );
    case 'CREATE_LIST':
      return (
        <EditList
          pending={customListsData.pending}
          error={customListsData.reason}
          list={{
            id: '',
            displayName: '',
            entities: [{ id: entityId, type: entityType }],
            thumbnailUrl:
              'https://cdnwmgdsp-a.akamaihd.net/dv/images/list/cl_list_5.png',
            templateUrl: 'https://www.opus.com',
          }}
          saveList={(list: ICustomListData): any => {
            return postCustomEntityList(list, (): void => {
              setViewState('ADD_TO_LIST');
            });
          }}
          close={close}
          cancel={(): void => {
            setViewState('ADD_TO_LIST');
            // if for some reason the create list view has thrown an error and the user cancels
            // the view, we need to reload the lists to clear the error message and show the
            // add to list view properly again
            if (customListsData.rejected) {
              fetchCustomLists();
            }
          }}
          saveButtonLabel="Save list and add"
          title="Create a new list"
        />
      );
    default:
      return null;
  }
};

export default connect<IOuterProps>(datasources)(AddEntityToListView as any);
